'use client'

import {
  getCharacterBadgeConfigListEnabled,
  getCharacterBadgeConfigListEnabledKey,
} from '@/apis/characterBadgeConfig'
import {
  buildCharacterBadgeConfigMap,
  getCharacterDisplayBadge,
} from '@/constant/character'
import { CharacterBadgeConfig } from '@/types/characterBadgeConfig'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import useSWR from 'swr'

const emptyBadgeConfigMap = new Map<string, CharacterBadgeConfig>()
const emptyBadgeConfigs: CharacterBadgeConfig[] = []

const CharacterBadgeConfigContext = createContext<{
  configs: CharacterBadgeConfig[]
  badgeConfigMap: Map<string, CharacterBadgeConfig>
}>({
  configs: [],
  badgeConfigMap: emptyBadgeConfigMap,
})

export function CharacterBadgeConfigProvider({ children }: PropsWithChildren) {
  const { data } = useSWR(
    getCharacterBadgeConfigListEnabledKey,
    getCharacterBadgeConfigListEnabled,
    {
      revalidateOnFocus: true,
    }
  )

  const configs = data || emptyBadgeConfigs
  const badgeConfigMap = useMemo(
    () => buildCharacterBadgeConfigMap(configs),
    [configs]
  )

  return (
    <CharacterBadgeConfigContext.Provider value={{ configs, badgeConfigMap }}>
      {children}
    </CharacterBadgeConfigContext.Provider>
  )
}

export const useCharacterBadgeConfigMap = () =>
  useContext(CharacterBadgeConfigContext).badgeConfigMap

export const useCharacterDisplayBadge = (badges?: string[] | null) => {
  const badgeConfigMap = useCharacterBadgeConfigMap()
  return useMemo(
    () => getCharacterDisplayBadge(badges, badgeConfigMap),
    [badges, badgeConfigMap]
  )
}
